
import RestX from 'highered-utils/restx';
import Vue, { PropType } from 'vue';
import { directive as onClickaway } from 'vue-clickaway';
import Fuse from 'fuse.js';
import { SelectOption } from './Select.vue';
import DialogService from '~/services/DialogService';

export default Vue.extend({
    name: 'SearchSelect',
    directives: { onClickaway },
    props: {
        value: {
            type: String,
            default: '',
        },
        options: {
            type: Array as PropType<SelectOption[]>,
            required: true,
        },
        placeholder: {
            type: String,
            default: '',
        },
        forseFocused: {
            type: Boolean,
            default: false,
        },
        disabled: Boolean,
        searchable: Boolean,
        custom: Boolean,
        inputClass: String,
    },
    data() {
        return {
            open: this.forseFocused ?? false,
            query: this.forseFocused ? this.value : '',
            selectedPlaceholder: '',
            selectedIndex: -1,
            fuse: null as Fuse<SelectOption> | null,
            isDialogOpen: false,
            schoolSaved: false,
        };
    },
    computed: {
        filteredOptions(): SelectOption[] {
            if (!this.searchable || !this.fuse) return this.options;
            if (!this.query) return this.options;

            return this.fuse.search(this.query.toLowerCase()).map(result => result.item);
        },
        selectedOption(): SelectOption | null {
            return this.options.find(option => option.value === this.value) || null;
        },
        selectedLabel(): string {
            return this.selectedOption ? this.selectedOption.label : '';
        },
    },
    methods: {
        away() {
            if (this.isDialogOpen) return;

            this.open = false;
        },
        scroll() {
            this.$nextTick(() => {
                const activeElement = this.$el.querySelector('div.option.active');
                if (!activeElement) return;

                activeElement.scrollIntoView({
                    block: 'center',
                });
            });
        },
        handleToggleInput(open?: boolean) {
            if (!this.open && this.disabled) return;
            this.open = typeof open !== 'undefined' ? open : !this.open;
        },
        handleSelectOption(option: SelectOption) {
            this.$emit('input', option.value, !!option.isCustom, this.options);
            this.query = option.label;
            this.open = false;
        },
        handleSelectCustom(label: string) {
            this.$emit('input', label, true, this.options);
            this.open = false;
        },
        handleKeyDown(evt: KeyboardEvent) {
            if (!this.open) {
                if (['ArrowDown', 'Enter'].includes(evt.key)) this.handleToggleInput();
                return;
            }
            switch (evt.key) {
                case 'ArrowUp':
                    this.selectedIndex = Math.max(-1, this.selectedIndex - 1);
                    this.scroll();
                    break;
                case 'ArrowDown':
                    this.selectedIndex = Math.min(
                        this.filteredOptions.length - 1,
                        this.selectedIndex + 1
                    );
                    this.scroll();
                    break;
                case 'Enter':
                    if (this.selectedIndex >= 0 && this.selectedIndex < this.filteredOptions.length)
                        this.handleSelectOption(this.filteredOptions[this.selectedIndex]);
                    else if (this.custom && !!this.query) this.handleSelectCustom(this.query);
                    break;
            }
        },
        handleInputBlur(evt: FocusEvent) {
            // if (evt.relatedTarget === this.$el) return;
            // if (this.custom && !!this.query) this.handleSelectCustom(this.query);
            // else this.handleToggleInput(false);
        },
        handleAddClick() {
            this.isDialogOpen = true;

            DialogService.openAddNewSchoolDialog({
                actions: [],
                listeners: {
                    close: ({ result }: { result?: string }) => {
                        this.isDialogOpen = false;
                        if (result === 'confirmed') {
                            this.schoolSaved = true;
                            this.selectedIndex = -1;
                            // this.$emit('school-saved', true);
                            // this.$emit('input', this.query, true);
                            this.$emit('close');
                        }
                        RestX.getInterfaces().dialogModal.getState().resolver.res();
                    },
                },
                then: () => {
                    this.isDialogOpen = false;
                },
                error: () => {
                    this.isDialogOpen = false;
                },
                cancel: () => {
                    this.isDialogOpen = false;
                },
            });
        },
    },
    watch: {
        open(value: boolean) {
            if (this.searchable && value) {
                this.savedSchool = false;
                this.$nextTick(() => {
                    const input = this.$el.querySelector('input');
                    if (input) input.focus();
                    this.query = this.query || this.selectedLabel || '';
                });
            } else {
                if (this.selectedOption) {
                    this.$emit('input', this.selectedOption.value, !!this.selectedOption.isCustom);
                    this.query = this.selectedOption.label;
                } else {
                    this.query = this.query || this.selectedLabel || '';
                    this.$emit('input', this.query, true);
                    this.selectedIndex = -1;
                }
            }
        },
        selectedIndex(value: number) {
            const opt = this.filteredOptions[value];
            this.selectedPlaceholder = opt ? opt.label : '';
        },
        disabled(value: boolean) {
            if (value) this.open = false;
        },
        query(value) {
            if (value && this.query) this.$emit('search', this.query);
        },
        options: {
            handler(newOptions: SelectOption[]) {
                const enhancedOptions = newOptions.map(option => {
                    const label = option.label;
                    const normalized = label.toLowerCase().replace(/[^\w\s]/g, '');
                    const words = normalized.split(/\s+/).filter(Boolean);
                    const reversed = words.slice().reverse().join(' ');
                    const acronym = (label.match(/\b[A-Z]/g) || []).join('').toLowerCase();

                    return {
                        ...option,
                        normalized,
                        reversed,
                        acronym,
                        searchString: [normalized, reversed, acronym].join(' '),
                    };
                });

                this.fuse = new Fuse(enhancedOptions, {
                    keys: ['searchString'],
                    threshold: 0.3,
                    ignoreLocation: true,
                    includeScore: false,
                });
            },
            immediate: true,
        },
    },
});
